/**
 * Deterministic parser for legacy contact-card messages.
 *
 * Mirrors the Python `format_contact_card_text(...)` shape so historical
 * rows persisted before the `message_type='contact_card'` migration
 * still render as cards in the inbox / analytics UIs. Modern rows ship
 * a populated `extra_data` and bypass this parser entirely.
 *
 * The function is intentionally strict: it returns `null` whenever the
 * `📇 Contatti di …` header line is missing, so plain-text bubbles are
 * never mis-classified as cards. The header regex anchors at the start
 * of a line and accepts an optional trailing colon.
 *
 * URL fields returned here are NOT sanitised — the React renderer is
 * responsible for the `https?:` allow-list before emitting an anchor.
 */

const HEADER_RX = /^📇\s*Contatti\s+di\s+(.+?):?\s*$/m;
const WA_RX = /^💬\s*WhatsApp:\s*(https:\/\/wa\.me\/(\d+))\s*$/m;
const PHONE_RX = /^📞\s*Telefono:\s*(.+?)\s*$/m;
const EMAIL_RX = /^✉️\s*Email:\s*(\S+)\s*$/m;
const LINK_RX = /^🔗\s*Link:\s*(\S+)\s*$/m;

/**
 * Parse a contact-card formatted body into a structured payload.
 *
 * @param {string|null|undefined} text - raw `message_text` value.
 * @returns {object|null} `{ name: { formatted }, phone?, phone_wa_id?,
 *   email?, referral_link?, wa_me_link?, source: 'formatted_text',
 *   kind: 'contact_card' }` or `null` when the body does not look like
 *   a contact card.
 */
export function parseContactCardText(text) {
  if (!text || typeof text !== 'string') return null;
  const headerMatch = text.match(HEADER_RX);
  if (!headerMatch) return null;

  const formatted = (headerMatch[1] || '').trim();
  if (!formatted) return null;

  const parts = formatted.split(/\s+/);
  const name = {
    formatted,
    first: parts[0] || null,
    last: parts.length > 1 ? parts.slice(1).join(' ') : null,
  };

  const payload = {
    kind: 'contact_card',
    source: 'formatted_text',
    name,
  };

  const waMatch = text.match(WA_RX);
  if (waMatch) {
    payload.wa_me_link = waMatch[1];
    payload.phone_wa_id = waMatch[2];
  }

  const phoneMatch = text.match(PHONE_RX);
  if (phoneMatch) {
    payload.phone = phoneMatch[1].trim();
  } else if (waMatch && waMatch[2]) {
    // wa.me deep-link → reconstruct an E.164 string for display.
    payload.phone = `+${waMatch[2]}`;
  }

  const emailMatch = text.match(EMAIL_RX);
  if (emailMatch) {
    payload.email = emailMatch[1].trim();
  }

  const linkMatch = text.match(LINK_RX);
  if (linkMatch) {
    payload.referral_link = linkMatch[1].trim();
  }

  // Require at least one identifying field beyond the header.
  if (!payload.phone && !payload.email && !payload.referral_link && !payload.wa_me_link) {
    return null;
  }
  return payload;
}

export default parseContactCardText;
